
import { useOnboarding } from "@/hooks/useOnboarding";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { TodoItem as TodoItemType } from "@/lib/types";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

interface TodoItemProps {
  todo: TodoItemType;
  stepId: string;
}

export function TodoItem({ todo, stepId }: TodoItemProps) {
  const { toggleTodo, isTodoCompleted } = useOnboarding();
  const completed = isTodoCompleted(stepId, todo.id);
  
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Copied to clipboard!"))
      .catch(() => toast.error("Failed to copy text"));
  };

  return (
    <motion.div 
      className={cn(
        "flex items-start space-x-3 p-3 rounded-lg transition-all",
        completed ? "bg-todo-complete" : "hover:bg-accent/50"
      )}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Checkbox
        id={todo.id}
        checked={completed}
        onCheckedChange={() => toggleTodo(stepId, todo.id)}
        className={cn(
          "mt-1 border-burgundy",
          completed && "bg-orange border-orange"
        )}
      />
      <div className="flex-1 flex flex-col space-y-2">
        <div className="flex items-start justify-between gap-2">
          <label
            htmlFor={todo.id}
            className={cn(
              "text-md leading-relaxed cursor-pointer flex-1",
              completed && "line-through text-gray-500"
            )}
          >
            {todo.text}
          </label>
          {todo.link && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 border-orange text-orange hover:bg-orange/10"
              onClick={() => window.open(todo.link, '_blank')}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {(todo.hint || todo.hintLink || todo.hintCopy) && (
          <div className="ml-1 text-sm text-slate-500 bg-slate-50 p-2 rounded">
            {todo.hint && <p>{todo.hint}</p>}
            
            <div className="flex flex-wrap gap-2 mt-1">
              {todo.hintLink && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => window.open(todo.hintLink, '_blank')}
                >
                  <ExternalLink className="mr-1 h-3 w-3" />
                  Open Link
                </Button>
              )}
              
              {todo.hintCopy && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => handleCopyToClipboard(todo.hintCopy!)}
                >
                  <Copy className="mr-1 h-3 w-3" />
                  Copy Text
                </Button>
              )}
            </div>
            
            {todo.hintCopy && (
              <div className="mt-2 bg-white p-2 rounded border border-slate-100 text-xs font-mono">
                {todo.hintCopy}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
